import { useTranslations } from 'next-intl';
import type { ProductSpec } from '@/lib/product-specs';

interface SpecGridProps {
  specs: ProductSpec[];
  className?: string;
}

/**
 * The quick-spec block beside the price: hairline-separated cells, two per row.
 *
 * Drawn as a single `bg-border` surface with 1px gaps rather than per-cell
 * borders, which is what gives the canvas its unbroken hairlines without
 * doubling them where cells meet.
 *
 * The label grey is the one place this deliberately departs from the canvas.
 * The canvas draws it at 45% ink, which composites to rgb(142,144,145) on the
 * #e7e7e5 cell — 2.59:1, well under the 4.5:1 WCAG AA needs for 11px text.
 * `text-muted` is 4.40:1, and rgb(104,104,104) is the lightest grey that clears
 * AA on this ground at all, so there is no version of this that both matches
 * the mock and stays legible. Measured and decided, not overlooked: leave it.
 */
export function SpecGrid({ specs, className }: SpecGridProps) {
  const t = useTranslations('product.spec');

  if (specs.length === 0) return null;

  return (
    <dl className={`grid grid-cols-2 gap-px overflow-hidden rounded border border-border bg-border ${className ?? ''}`}>
      {specs.map((spec) => (
        <div key={spec.code} className="flex flex-col gap-0.5 bg-surface-subtle px-[18px] py-3.5">
          <dt className="text-[11px] uppercase tracking-[0.08em] text-muted">
            {spec.labelKey ? t(spec.labelKey) : spec.label}
          </dt>
          <dd className="text-[14.5px] font-semibold text-foreground">
            {spec.valueKey ? t(spec.valueKey) : spec.value}
          </dd>
        </div>
      ))}

      {/* An odd cell count would otherwise leave the grid's own `bg-border`
          showing as a stray dark rectangle in the last row. */}
      {specs.length % 2 === 1 && <div className="bg-surface-subtle" aria-hidden="true" />}
    </dl>
  );
}

interface SpecTableProps {
  specs: ProductSpec[];
}

/**
 * The full attribute list inside the "Specifikationer" accordion.
 *
 * A two-column definition list rather than the grid above: this one is
 * exhaustive and variable in length, so it reads down a page instead of
 * tiling. Labels collapse above values below `sm`, where a fixed label column
 * would leave the values nowhere to go.
 */
export function SpecTable({ specs }: SpecTableProps) {
  const t = useTranslations('product.spec');

  if (specs.length === 0) return null;

  return (
    <dl className="grid gap-x-4 gap-y-2.5 text-sm sm:grid-cols-[180px_minmax(0,1fr)]">
      {specs.map((spec) => (
        <div key={spec.code} className="contents">
          <dt className="text-muted">{spec.labelKey ? t(spec.labelKey) : spec.label}</dt>
          <dd className="font-medium text-foreground/85">
            {spec.valueKey ? t(spec.valueKey) : spec.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
